import { z } from 'zod';
import { cents, entityBase, id, timestamp } from './common.js';

export const billingProfile = entityBase.extend({
  stripeCustomerId: z.string().nullable(),
  achVerified: z.boolean(),
  cardOnFile: z.boolean(),
  gustoCompanyId: z.string().nullable(),
});
export type BillingProfile = z.infer<typeof billingProfile>;

export const paymentMethodType = z.enum(['ach', 'card']);
export type PaymentMethodType = z.infer<typeof paymentMethodType>;

export const paymentMethodRef = entityBase.extend({
  billingProfileId: id,
  stripePaymentMethodId: z.string().min(1),
  methodType: paymentMethodType,
  isDefault: z.boolean(),
});
export type PaymentMethodRef = z.infer<typeof paymentMethodRef>;

export const earningStatus = z.enum(['accrued', 'payable', 'included_in_payroll', 'paid']);
export type EarningStatus = z.infer<typeof earningStatus>;

export const shiftEarning = entityBase.extend({
  bookingId: id,
  professionalId: id,
  grossCents: cents,
  platformFeeCents: cents,
  netCents: cents,
  status: earningStatus,
  /** Historical earnings may predate work approval; new approvals always provide these snapshots. */
  workRecordId: id.nullable(),
  approvedMinutes: z.number().int().positive().nullable(),
  hourlyRateCents: cents.nullable(),
  officeFeeBasisPoints: z.number().int().min(0).max(10000),
  officeFeeCents: cents,
  officeTotalCents: cents.nullable(),
});
export type ShiftEarning = z.infer<typeof shiftEarning>;

export const payrollRunStatus = z.enum(['draft', 'submitted', 'paid', 'failed']);
export type PayrollRunStatus = z.infer<typeof payrollRunStatus>;

export const payrollRun = entityBase.extend({
  periodStart: timestamp,
  periodEnd: timestamp,
  status: payrollRunStatus,
  gustoRunId: z.string().nullable(),
  submittedAt: timestamp.nullable(),
});
export type PayrollRun = z.infer<typeof payrollRun>;

export const payrollRunLine = entityBase.extend({
  payrollRunId: id,
  shiftEarningId: id,
});
export type PayrollRunLine = z.infer<typeof payrollRunLine>;

/** True when both ACH and card are on file (posting gate). */
export function billingReady(profile: Pick<BillingProfile, 'achVerified' | 'cardOnFile'>): boolean {
  return profile.achVerified && profile.cardOnFile;
}
